import { useState } from 'react';
import { ChevronRight, Repeat2, Target } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { awaitsDecision, isNotStarted } from '../../lib/agent-task-buckets';
import { taskStatusBadge } from '../../lib/task-status';
import { maestroActionSentence, workingSentence, type MaestroActionRow, type QuickViewRow, type QuickViewTask } from '../../lib/island-quickview';
import { timeAgo } from '../../lib/notification-time';
import { AgentDot } from '../AgentDot';
import { MaestroMark } from './MaestroMark';
import { Badge, StatusBadge } from './ui';
import { TaskDecisionPrompts } from './tasks/TaskDecisionPrompts';
import { OutreachKindBadge } from './tasks/OutreachKindBadge';
import styles from './AgentsIslandQuickViewRow.module.css';

/** BI for this card — no `page_version` in use on this surface (every `logPageClickBi` call here
 *  already omits it), so the decision block gets the same bare `page_name`. */
const ISLAND_PAGE = { page_name: 'agents-island-quickview' };

// ONE row of the agents-island quick-view. Its own file because the panel around it drags in the task
// modals (and, through them, the markdown renderer), and the row's affordance — the part a reader has to
// be able to tell apart at a glance — is worth being able to render on its own in a test.

const INK = 'var(--sym-ink)';
const INK_2 = 'var(--ds-text-secondary)';

const META_LINE: React.CSSProperties = {
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 600,
  letterSpacing: '-0.02em',
  color: INK_2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};


/** The visually-hidden sentence a folded row hands a screen reader — the deck and the chevron are
 *  pixels, and pixels are not announced. Standard clip-rect rather than `display: none`, which would
 *  take it out of the accessibility tree too. */
const SR_ONLY: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/** One peeking card edge under a folded row. Two of them, each narrower than the last, are what make the
 *  row read as a DECK rather than a single card — the structural half of the fold's signal, which a count
 *  inside a status badge ("2 Not started") never carried: a reader parses that as a status, not a pile. */
function stackEdge(inset: number, bottom: number): React.CSSProperties {
  return {
    position: 'absolute',
    left: inset,
    right: inset,
    bottom,
    height: 6,
    borderRadius: '0 0 10px 10px',
    border: '1px solid var(--ds-separator-default)',
    borderTop: 'none',
    pointerEvents: 'none',
  };
}

/** Exported for the fold-affordance test, which SSR-renders a folded row beside a single-run one and
 *  asserts the two do not look alike. */
export function QuickViewRowButton({
  task,
  agentName,
  onOpen,
}: {
  task: QuickViewRow;
  agentName?: string;
  onOpen: (task: QuickViewRow) => void;
}) {
  const { t } = useTranslation();
  // A collapsed origin fold — the Tasks screen's `TaskOriginGroup`, in peek form: the newest task's title
  // and status, the origin's glyph marking WHY several rows are one row, and the fold's shared outcome as
  // "{count} {status}" on the same badge a single task of that status wears.
  const grouped = task.runCount > 1;
  // Laid out and never started, derived exactly as on the Tasks screen — the bubble must not be the one
  // surface still saying "In progress" about a task nobody has been put on.
  const notStarted = isNotStarted(task);
  // The one thing this row cannot know until the re-read lands: an answer landed a moment ago. Same
  // shape as the list row's `answeredHere` — never set on a grouped row, since a fold offers no chips.
  const [answeredHere, setAnsweredHere] = useState(false);
  // Blocked on an open question, derived exactly as on the Tasks screen. This card's population IS the
  // waiting bucket, and the promotion that puts a PENDING blocked task in it happens on the row — so
  // without this the bubble would list a row under "Action needed" wearing an "In progress" chip.
  const awaitingDecision = awaitsDecision(task) && !answeredHere;
  const groupBadge = grouped ? taskStatusBadge(task.status, { notStarted, awaitingDecision }) : null;
  // An outreach row is titled by WHO SENT IT — its subject is a person, not a piece of work. Same rule,
  // same copy keys as the list's `TaskCard`, because this row is a peek at that list.
  const title = task.isOutreach
    ? task.senderName
      ? t('tasks.outreach.title', { name: task.senderName })
      // "Incoming message", not "Untitled task" — an inbound outreach whose sender could not be resolved
      // is neither a task nor untitled, and the phones already said so. Same key, same 37 translations.
      : t('tasks.outreach.untitled')
    : task.title || t('tasks.untitledTask');
  // A fold does not open a task — it opens the Tasks list on its own tab, because the peek cannot expand
  // several runs in place (see `onOpen`). That is the right behaviour, but it was UNPREDICTABLE: a folded
  // row looked exactly like the single-task row above it, so two identical-looking rows did different
  // things on click. The signal has to be STRUCTURAL — the row is drawn as the top card of a deck (its own
  // outline plus two narrower card edges peeking below) and wears the same trailing chevron the panel's
  // own nav control wears, so it reads as "several things, and this goes somewhere" before it is touched.
  return (
    <div style={{ position: 'relative', paddingBottom: grouped ? 13 : 0 }}>
    <button
      type="button"
      onClick={() => onOpen(task)}
      // The fold's count, on the element itself: the deck is drawn in pixels, so this is what a test can
      // hold on to without re-deriving the branch it is checking.
      data-fold-count={grouped ? task.runCount : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 12,
        // One border's worth of padding is given back to the outline, so a folded row's content sits on
        // exactly the same box as a single row's and the list does not jump between the two.
        padding: grouped ? '9px' : '10px',
        borderRadius: 12,
        textAlign: 'left',
        width: '100%',
        border: grouped ? '1px solid var(--ds-separator-default)' : 'none',
        background: 'transparent',
        font: 'inherit',
        cursor: 'pointer',
        color: INK,
      }}
    >
      {/* 14/18 rather than the frame's 14/14: an ellipsizing run needs a line box taller than its own
          glyphs, or `overflow: hidden` shaves the descenders. */}
      {/* An outreach row is shaped like a task on purpose (same row, same statuses), which is exactly why
          it has to SAY it isn't one — the Tasks list's own card already does this and the peek did not,
          so the same row read as a task here and as a message one screen over. */}
      {task.isOutreach && !grouped ? (
        <span style={{ display: 'flex' }}>
          <OutreachKindBadge />
        </span>
      ) : null}
      <span style={{ fontSize: 14, fontWeight: 'var(--sym-weight-medium)', lineHeight: '18px', letterSpacing: '-0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {title}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        {/* The agent tag grows to fill the row so the status badge is pushed to the trailing (right) edge;
            `minWidth: 0` lets the name ellipsize inside it rather than shoving the badge off-row. */}
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
          {task.agentId ? <AgentDot id={task.agentId} size={12} /> : null}
          {agentName ? <span style={META_LINE}>{agentName}</span> : null}
        </span>
        {grouped && groupBadge ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {/* The routines mark is `repeat-2` app-wide (see `RoutinesSummary` / `TaskOriginGroup`); the
                goals mark is `target`, as on `TaskOriginLine`. */}
            <span style={{ display: 'inline-flex', color: 'var(--ds-icon-primary)' }} aria-hidden>
              {task.pulseIdentityId ? <Repeat2 size={14} /> : <Target size={14} />}
            </span>
            <Badge
              variant={groupBadge.variant}
              label={t('tasks.routineGroup.runsStatus', { count: task.runCount, status: t(groupBadge.labelKey) })}
            />
            {/* The same mark the bucket control above wears, for the same reason: this row leaves the
                panel. A single-run row opens in place and gets none. */}
            <ChevronRight size={16} aria-hidden className="dir-flip" style={{ color: INK_2 }} />
          </span>
        ) : (
          <StatusBadge status={task.status} notStarted={notStarted} awaitingDecision={awaitingDecision} outreach={task.isOutreach} />
        )}
      </span>
      {/* The deck and the chevron say "group, and it leaves here" in pixels only. A screen reader gets the
          same two facts in words, or the fix reaches sighted readers alone. */}
      {grouped ? <span style={SR_ONLY}>{t('agentsIsland.quickView.groupRow', { count: task.runCount })}</span> : null}
    </button>
    {/* The question this row is held on, answered in place — never on a folded row: a fold's badge
        already says how many of its runs need you, and there is no single decision for a chip to name.
        Outside the button for the same reason it is outside the Tasks-list card: a button cannot nest a
        button. No goal title travels with this row, so `origin={null}` — the unavailable state renders
        with no link rather than an unresolvable one. */}
    {!grouped && (
      <div style={{ padding: '0 10px 10px' }}>
        <TaskDecisionPrompts task={task} origin={null} onAnsweredChange={setAnsweredHere} page={ISLAND_PAGE} />
      </div>
    )}
    {grouped ? (
      <>
        <span aria-hidden style={stackEdge(6, 7)} />
        <span aria-hidden style={stackEdge(12, 0)} />
      </>
    ) : null}
    </div>
  );
}

/**
 * ONE row of the blue "agents at work" card — AGENT-first, unlike the task-first peek row above, because
 * the blue state's subject is who is working right now:
 *
 *   - the agent's avatar wearing a pulsing live ring (its loop is executing by selection — only
 *     `runInFlight === true` tasks become working rows), the agent's name in bold; a task with no
 *     `agentId` is the Maestro's, shown under the user's own (renameable) assistant name, which the
 *     caller resolves from the same source the rest of the chrome uses (`useAgentName`).
 *   - a REVIEWING mini-chip when the task is IN_REVIEW — a live loop on an IN_REVIEW task is by
 *     definition the reviewer's.
 *   - trailing: compact elapsed-since-activity ("2m"), from `lastActivityAt` when the row carries it.
 *   - the hero sentence, by the shared chain (`workingSentence`): the agent's own `currentAction` →
 *     the first unticked plan step → the localized "Working on: {title}" in the muted ink.
 *   - the task title as a secondary line — OMITTED when the sentence already is the title fallback,
 *     so no row says its title twice.
 *
 * Tap opens the task, exactly as a peek row does. Exported for the behavior tests.
 */
export function WorkingRowButton({
  task,
  agentLabel,
  onOpen,
}: {
  task: QuickViewTask;
  /** The resolved display name: the roster's name for `task.agentId`, or the Maestro's own. */
  agentLabel: string;
  onOpen: (task: QuickViewTask) => void;
}) {
  const { t } = useTranslation();
  const sentence = workingSentence(task);
  const title = task.title || t('tasks.untitledTask');
  const elapsed = timeAgo(task.lastActivityAt ?? null);
  const heroText = sentence.kind === 'title' ? t('agentsIsland.quickView.workingOn', { title }) : sentence.text;
  return (
    <button
      type="button"
      onClick={() => onOpen(task)}
      data-working-row
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 6,
        padding: '10px',
        borderRadius: 12,
        textAlign: 'left',
        width: '100%',
        border: 'none',
        background: 'transparent',
        font: 'inherit',
        cursor: 'pointer',
        color: INK,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span className={styles.liveRing} aria-hidden>
          {/* The Maestro is deliberately OUT of the agent colour system (see FileCreatedBy) — an
              unattributed live run is the team lead's, never a hued dot pretending to be a teammate. */}
          {task.agentId ? <AgentDot id={task.agentId} size={20} /> : <MaestroMark size={20} />}
        </span>
        <span
          style={{
            fontSize: 14,
            fontWeight: 'var(--sym-weight-semibold)',
            lineHeight: '18px',
            letterSpacing: '-0.02em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {agentLabel}
        </span>
        {task.status === 'IN_REVIEW' ? (
          <Badge variant="in-progress" label={t('agentsIsland.quickView.reviewing')} style={{ textTransform: 'uppercase', flexShrink: 0 }} />
        ) : null}
        <span style={{ flex: 1 }} />
        {elapsed ? <span style={{ ...META_LINE, flexShrink: 0 }}>{elapsed}</span> : null}
      </span>
      {/* 14/18 rather than 14/14 for the same reason as the peek row: an ellipsizing line needs a box
          taller than its glyphs. The title-fallback sentence reads in the muted ink — it is the chain's
          last resort, not the agent's own words. */}
      <span
        style={{
          fontSize: 14,
          fontWeight: 'var(--sym-weight-medium)',
          lineHeight: '18px',
          letterSpacing: '-0.02em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: sentence.kind === 'title' ? INK_2 : INK,
        }}
      >
        {heroText}
      </span>
      {sentence.kind !== 'title' ? (
        <span style={META_LINE}>{title}</span>
      ) : null}
    </button>
  );
}

/**
 * ONE Maestro-ACTION row of the blue card — the Maestro personally at work on something that is not a
 * task (a Wixel render, a free-run agent loop). Same anatomy as a working row's head — the Maestro's mark
 * in the pulsing live ring, the user's own (renameable) assistant name in bold, compact elapsed from
 * `startedAt` when the record carries it — but deliberately LESS below it:
 *
 *   - the sentence is the action's `label`, else a localized per-kind fallback ("Creating a design with
 *     Wixel" / "Working for you") — an empty label falls back, it never renders an empty line;
 *   - NO secondary title line and NO reviewing chip — there is no task behind the row to name or review.
 *
 * Tap opens the MAIN CHAT (the surface's existing open-chat navigation): the action lives on the maestro
 * conversation, so the chat is where its progress and outcome will land. Exported for the behavior tests.
 */
export function MaestroActionRowButton({
  action,
  agentLabel,
  onOpen,
}: {
  action: MaestroActionRow;
  /** The Maestro's resolved display name — the same source the rest of the chrome uses. */
  agentLabel: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const sentence = maestroActionSentence(action);
  const elapsed = timeAgo(action.startedAt ?? null);
  return (
    <button
      type="button"
      onClick={onOpen}
      data-maestro-action-row={action.kind}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 6,
        padding: '10px',
        borderRadius: 12,
        textAlign: 'left',
        width: '100%',
        border: 'none',
        background: 'transparent',
        font: 'inherit',
        cursor: 'pointer',
        color: INK,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span className={styles.liveRing} aria-hidden>
          <MaestroMark size={20} />
        </span>
        <span
          style={{
            fontSize: 14,
            fontWeight: 'var(--sym-weight-semibold)',
            lineHeight: '18px',
            letterSpacing: '-0.02em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {agentLabel}
        </span>
        <span style={{ flex: 1 }} />
        {elapsed ? <span style={{ ...META_LINE, flexShrink: 0 }}>{elapsed}</span> : null}
      </span>
      <span
        style={{
          fontSize: 14,
          fontWeight: 'var(--sym-weight-medium)',
          lineHeight: '18px',
          letterSpacing: '-0.02em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {sentence.kind === 'label' ? sentence.text : t(sentence.key)}
      </span>
    </button>
  );
}
